import React, { useEffect, useRef, useState } from 'react';
import TrajectoryBackground from './components/TrajectoryBackground';
import ConnorPFPImage from './assets/connor_pfp_image.jpg';
import ConnorVideo from './assets/connor_video.mp4';
import ConnorVideoPoster from './assets/connor_video_poster.jpg';
import './App.css';

const experience = [
  {
    title: 'Research Intern',
    company: 'Databricks',
    dates: 'May 2026 — Present',
    mark: 'DB',
    bullets: ['Mosaic AI Research', 'Genie / Genie One'],
  },
  {
    title: 'Research Assistant',
    company: 'UC Berkeley · SkyLab / BAIR',
    dates: 'Sep 2024 — Present',
    mark: 'B',
    bullets: [
      'Inference-time scaling & compound AI systems.',
      'Advised by Matei Zaharia & Jared Quincy Davis.',
    ],
  },
  {
    title: 'Software Engineer Intern',
    company: 'Amazon Web Services',
    dates: 'May — Aug 2025',
    mark: 'aws',
    bullets: ['Builder Insights', 'On-call automation.'],
  },
  {
    title: 'Software Engineer Intern',
    company: 'Strategic Building Innovation · bimSCORE',
    dates: 'May — Aug 2024',
    mark: 'sbi',
    bullets: ['End-to-end RAG agent development.', 'Advised by Calvin Kam.'],
  },
  {
    title: 'Mentorship in Data Science',
    company: 'Stanford University',
    dates: 'Jan — Jun 2023',
    mark: 'S',
    bullets: ['Machine learning & computer vision.', 'CS 229 / CS 231N'],
  },
];

function Arrow() {
  return (
    <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 19 19 5M5 5h14v14" />
    </svg>
  );
}

function Portrait({ isReady, setIsReady }) {
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current?.complete) setIsReady(true);
  }, [setIsReady]);

  return (
    <figure className={`portrait${isReady ? ' is-ready' : ''}`}>
      <img
        ref={imageRef}
        src={ConnorPFPImage}
        alt="Connor Chow at UC Berkeley"
        width="1600"
        height="1600"
        fetchpriority="high"
        onLoad={() => setIsReady(true)}
        onError={() => setIsReady(true)}
      />
    </figure>
  );
}

function PersonalFilm() {
  const videoRef = useRef(null);
  const previousVolumeRef = useRef(35);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [volume, setVolume] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) video.pause();
    }, { threshold: 0.15 });
    const pauseWhenHidden = () => { if (document.hidden) video.pause(); };
    observer.observe(video);
    document.addEventListener('visibilitychange', pauseWhenHidden);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', pauseWhenHidden);
    };
  }, []);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) {
      video.pause();
      return;
    }
    try {
      await video.play();
      setHasError(false);
    } catch (error) {
      if (error.name !== 'AbortError') setHasError(true);
    }
  };

  const changeVolume = (value) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.max(0, Math.min(100, Number(value)));
    video.volume = next / 100;
    video.muted = next === 0;
    setVolume(next);
    if (next > 0) previousVolumeRef.current = next;
  };

  const syncVolume = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = video.muted ? 0 : Math.round(video.volume * 100);
    setVolume(next);
    if (next > 0) previousVolumeRef.current = next;
  };

  return (
    <div className="film">
      <div className="film-player">
        <div className="film-frame">
          <video
            id="personal-film"
            ref={videoRef}
            muted={volume === 0}
            loop
            playsInline
            preload="none"
            poster={ConnorVideoPoster}
            aria-label="A personal video edit by Connor Chow"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onVolumeChange={syncVolume}
            onError={() => setHasError(true)}
          >
            <source src={ConnorVideo} type="video/mp4" />
          </video>
          <button
            className="film-surface"
            type="button"
            onClick={togglePlayback}
            aria-controls="personal-film"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          />
        </div>
        <div className="film-controls" role="group" aria-label="Video controls">
          <button
            className="film-toggle"
            type="button"
            onClick={togglePlayback}
            aria-controls="personal-film"
            aria-label={isPlaying ? 'Pause personal film' : 'Play personal film'}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              {isPlaying ? <path d="M9 6v12M15 6v12" /> : <path d="m9 5 11 7-11 7V5Z" />}
            </svg>
          </button>
          <input
            className="film-volume"
            type="range"
            min="0"
            max="100"
            step="1"
            value={volume}
            onChange={(event) => changeVolume(event.target.value)}
            aria-label="Video volume"
            aria-valuetext={`${volume}%`}
            aria-orientation="vertical"
            aria-controls="personal-film"
          />
          <button
            className="film-mute"
            type="button"
            onClick={() => changeVolume(volume === 0 ? previousVolumeRef.current : 0)}
            aria-controls="personal-film"
            aria-label={volume === 0 ? 'Unmute personal film' : 'Mute personal film'}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m11 5-5 4H3v6h3l5 4V5Z" />
              {volume === 0 ? <path d="m16 9 6 6m0-6-6 6" /> : <path d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14" />}
            </svg>
          </button>
        </div>
      </div>
      {hasError && <p className="film-error" role="status">Unable to play. Please try again.</p>}
    </div>
  );
}

export default function PersonalWebsite() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [portraitReady, setPortraitReady] = useState(false);
  const [skipEntrance, setSkipEntrance] = useState(() => Boolean(window.location.hash && window.location.hash !== '#home'));
  const navRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const dismissMenu = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuRef.current?.focus();
      }
    };
    const dismissOutside = (event) => {
      if (!navRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', dismissMenu);
    document.addEventListener('pointerdown', dismissOutside);
    return () => {
      document.removeEventListener('keydown', dismissMenu);
      document.removeEventListener('pointerdown', dismissOutside);
    };
  }, [menuOpen]);

  return (
    <div
      className={`site-shell${portraitReady ? ' portrait-ready' : ''}${skipEntrance ? ' skip-entrance' : ''}`}
      id="home"
      onPointerDownCapture={() => setSkipEntrance(true)}
      onWheelCapture={() => setSkipEntrance(true)}
      onKeyDownCapture={() => setSkipEntrance(true)}
    >
      <TrajectoryBackground />
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="site-header">
        <nav className="navigation page-width" aria-label="Main navigation" ref={navRef}>
          <a href="#home" className="wordmark" aria-label="Connor Chow home" onClick={() => setMenuOpen(false)}>
            <span className="brand-icon" aria-hidden="true"><img src={`${process.env.PUBLIC_URL}/duckicon.jpeg`} alt="" width="564" height="564" /></span>
            <span>connor chow</span>
          </a>
          <button
            ref={menuRef}
            type="button"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="navigation-links"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'Close' : 'Menu'}<span aria-hidden="true">{menuOpen ? '−' : '+'}</span>
          </button>
          <div id="navigation-links" className={`navigation-links ${menuOpen ? 'is-open' : ''}`}>
            <a href="#publications" onClick={() => setMenuOpen(false)}>Publications</a>
            <a href="#experience" onClick={() => setMenuOpen(false)}>Experience</a>
            <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
            <a className="nav-contact" href="#contact" onClick={() => setMenuOpen(false)}>Contact <Arrow /></a>
          </div>
        </nav>
      </header>

      <main id="main" className="page-layout page-width">
        <div className="identity-rail">
          <div className="identity-copy">
            <h1>hi, i’m connor.</h1>
            <p>
              <span className="bio-summary">researching agentic and compound ai systems</span>
              <span className="bio-affiliations">databricks · aws · uc berkeley (skylab / bair)</span>
            </p>
          </div>
        </div>

        <div className="content-column">
          <Portrait isReady={portraitReady} setIsReady={setPortraitReady} />

          <section className="content-section" id="publications" aria-labelledby="publications-title">
            <h2 id="publications-title">Publications</h2>
            <div className="publication-list">
              <article className="publication">
                <h3>Prompting as an LLM Distribution Dial</h3>
                <p className="publication-meta">2026 · arXiv preprint</p>
                <details className="publication-authors">
                  <summary>Authors</summary>
                  <p><strong>Connor Chow*</strong>, Jason Lee*, Eric Khodorenko, Lakshya A Agrawal, Jared Quincy-Davis, Lingjiao Chen, Dan Klein, Ion Stoica, Matei Zaharia</p>
                  <p>* Equal contribution</p>
                </details>
              </article>
              <article className="publication">
                <h3>Ember, an inference-time scaling architecture framework</h3>
                <p className="publication-meta">2025 · Open source</p>
                <div className="publication-links">
                  <a href="https://mithril.ai/blog/introducing-project-ember-a-compositional-framework-for-compound-ai-systems" target="_blank" rel="noreferrer" className="text-link">Project <Arrow /></a>
                  <a href="https://github.com/pyember/ember" target="_blank" rel="noreferrer" className="text-link">Code <Arrow /></a>
                  <details className="publication-authors">
                    <summary>Authors</summary>
                    <p>Jared Quincy Davis, Marquita Ellis, Diana Arroyo, Pravein Govindan Kannan, Paul Castro, Siddharth Sharma, Parth Asawa, Alan Zhu, <strong>Connor Chow</strong>, et al.</p>
                  </details>
                </div>
              </article>
            </div>
          </section>

          <section className="content-section" id="experience" aria-labelledby="experience-title">
            <h2 id="experience-title">Experience</h2>
            <ol className="experience-list">
              {experience.map((item) => (
                <li className="experience-row" key={`${item.company}-${item.title}`}>
                  <div className="company-mark" aria-hidden="true">{item.mark}</div>
                  <div className="experience-content">
                    <h3>{item.title}</h3>
                    <p className="experience-company">{item.company}</p>
                    <p className="experience-dates">{item.dates}</p>
                    <ul className="experience-bullets">
                      {item.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="content-section" id="about" aria-labelledby="about-title">
            <h2 id="about-title">About</h2>
            <p className="about-copy">i like matcha, keyboards, and video editing.<br />sometimes i ski, run, and climb.</p>
            <PersonalFilm />
          </section>

          <section className="content-section contact-section" id="contact" aria-labelledby="contact-title">
            <h2 id="contact-title">Contact</h2>
            <div className="contact-links">
              <button type="button" className="text-link" aria-expanded={showEmail} aria-controls="contact-email" onClick={() => setShowEmail((shown) => !shown)}>Email <Arrow /></button>
              <a href="https://www.linkedin.com/in/connorchow/" target="_blank" rel="noreferrer" className="text-link">LinkedIn <Arrow /></a>
              <a href="https://github.com/conconchowchow" target="_blank" rel="noreferrer" className="text-link">GitHub <Arrow /></a>
            </div>
            <div className="contact-email" id="contact-email" aria-live="polite">
              {showEmail && <a href="mailto:connorchow@berkeley.edu">connorchow [at] berkeley [dot] edu</a>}
            </div>
          </section>
        </div>
      </main>
      <footer className="site-footer page-width"><span>© {new Date().getFullYear()} Connor Chow</span><a href="#home" aria-label="Back to top"><Arrow /></a></footer>
    </div>
  );
}
